import type { PromotionType } from '@VelocitySquared/sunny16-commerce-dataobjects/enums'

/**
 * Logical/visual state of a reward tier relative to the cart's current subtotal.
 * Drives the RewardsTracker node styling (locked / current / unlocked).
 */
export enum RewardTierState {
  Locked = 'locked',
  Current = 'current',
  Unlocked = 'unlocked'
}

/**
 * A single reward tier rendered by the RewardsTracker — a presentation-ready view derived from a
 * `RewardPromotionStorefrontDto` plus the cart subtotal and the subject's proof type.
 */
export interface RewardTier {
  promotionId: string
  type: PromotionType
  /** Bundle this tier's promotion grants (`additionalBundleId` = `CatalogBundle.bundleId`), if any. */
  additionalBundleId?: string
  minimumSubtotal: number
  /** Customer-facing label — Contentful `rewardTrackerLabel`, falling back to `retailName` / `nameInternal`. */
  label: string
  /** Label shown once unlocked — Contentful `rewardTrackerUnlockedLabel`, falling back to `label`. */
  unlockedLabel?: string
  /** Resolved node icon URL — Contentful `rewardTrackerIcon`; falls back to a `PromotionType` default. */
  iconUrl?: string
  state: RewardTierState
  /** Dollars still needed to unlock this tier; `0` once unlocked. */
  amountToUnlock: number
}

/**
 * The subtotal-independent fields of a {@link RewardTier} — everything resolvable from the catalog
 * payload + Contentful content without knowing the cart subtotal. Computed once (filter + sort +
 * content resolution) by the cached `rewardLadder` getter; `rewardTiers(subtotal)` then stamps the
 * per-subtotal `state` and `amountToUnlock` onto each entry.
 */
export type RewardLadderTier = Omit<RewardTier, 'state' | 'amountToUnlock'>
